/**
 * ActionHandler Service
 * Handles form submissions and other UI actions using the existing action system
 */

import { triggerAction } from '@js/utils/actionHelpers';

class ActionHandler {
    constructor() {
        // Enable debug mode automatically in development
        this.isDebug = Boolean(window.enableDebugMode);
        this.init();
    }

    init() {
        this.setupGlobalListeners();
        this.setupFormListeners();
        if (window.enableDebugMode) { console.log(`[ActionHandler] Initialized ${this.isDebug ? '(Debug Mode Enabled)' : ''}`); }
    }

    /**
     * Setup global event listeners
     */
    setupGlobalListeners() {
        // Listen for Alpine.js element-action events from the action mixin
        document.addEventListener('element-action', this.handleElementAction.bind(this));
        
        // Listen for custom action events
        document.addEventListener('action:trigger', this.handleActionTrigger.bind(this));
        
        // Listen for form submission events
        document.addEventListener('submit', this.handleFormSubmit.bind(this));
        
        // Listen for button clicks with data-action attributes
        document.addEventListener('click', this.handleActionClick.bind(this));
    }

    /**
     * Setup form-specific listeners
     */
    setupFormListeners() {
        // Customer login forms
        this.setupFormHandler('[data-customer-login-form]', 'customerLogin');
        
        // Customer registration forms
        this.setupFormHandler('[data-customer-register-form]', 'customerRegister');
        
        // Add to cart forms
        this.setupFormHandler('[data-add-to-cart-form]', 'addToCart');
        
        // Newsletter forms
        this.setupFormHandler('[data-newsletter-form]', 'customerSubscribe');
    }

    /**
     * Setup handler for specific form type
     */
    setupFormHandler(selector, actionName) {
        const forms = document.querySelectorAll(selector);
        forms.forEach(form => {
            form.addEventListener('submit', (e) => {
                e.preventDefault();
                this.handleFormByAction(form, actionName);
            });
        });
    }

    /**
     * Handle Alpine.js element-action events from the action mixin
     */
    async handleElementAction(event) {
        const { blockId, settings, triggerType, element } = event.detail;
        
        if (window.enableDebugMode) {
            console.log(`[ActionHandler] Element action received:`, event);
        }

        try {
            // Map action preset to actual action name
            const actionName = this.mapActionPreset(settings.action_preset);
            
            if (actionName) {
                // Prepare action data
                const actionData = {
                    blockId,
                    blockType: settings.block_type || 'unknown',
                    triggerType,
                    element,
                    settings,
                    // Extract product data if available (for add to cart actions)
                    ...(this.extractProductData(element, settings))
                };

                await this.executeAction(actionName, actionData);
            } else if ((settings.action_preset === 'custom' || !settings.action_preset) && settings.custom_actions) {
                // Handle custom actions — build the same full context as preset actions
                const actionData = {
                    blockId,
                    blockType: settings.block_type || 'unknown',
                    triggerType,
                    element,
                    settings,
                    ...(this.extractProductData(element, settings))
                };
                await this.handleCustomActions(settings.custom_actions, actionData);
            }
        } catch (error) {
            console.error(`[ActionHandler] Error handling element action:`, error);
        }
    }

    /**
     * Map action presets to actual action names
     */
    mapActionPreset(actionPreset) {
        const presetMap = {
            'add_to_cart': 'addToCart',
            'add_to_cart_open_cart': 'addToCartAndCartOpen',
            'open_cart': 'cartOpen',
            'close_cart': 'cartClose',
            'toggle_cart': 'cartToggle',
            'wishlist_toggle': 'wishlistToggle',
            'scroll_to_top': 'scrollToTop',
            'print_info_toggle': 'printInfoToggle',
            'check_store_availability': 'clickCollectToggle'
        };
        
        return presetMap[actionPreset] || null;
    }

    /**
     * Handle custom actions (comma-separated list)
     * Actions are dispatched without awaiting since custom arc: events don't
     * fire success/error responses, which would stall the loop indefinitely.
     */
    handleCustomActions(customActions, context) {
        if (!customActions) return;
        
        const actions = customActions.split(',').map(action => action.trim()).filter(Boolean);
        
        for (const actionName of actions) {
            try {
                this.executeAction(actionName, context);
            } catch (error) {
                console.error(`[ActionHandler] Error executing custom action ${actionName}:`, error);
            }
        }
    }

    /**
     * Extract product data from element context for cart actions
     */
    extractProductData(element, _settings) {
        const productData = {};
        
        // Try to find product form in the vicinity — same selectors used by button-link.liquid
        const productForm = element?.closest('form.shopify-product-form') ||
                            element?.closest('form[action*="/cart/add"]') ||
                            document.querySelector('form.shopify-product-form') ||
                            document.querySelector('form.add-to-cart-pdp-form') ||
                            document.querySelector('form[action*="/cart/add"]');

        if (productForm) {
            // Mirror the variant detection used in button-link.liquid
            const variantSelectField = productForm.querySelector('select[id^=variant-select-][name=id]');
            const variantFallbackField = productForm.querySelector('input[name=id], select[name=id]');
            const variantInput = variantSelectField || variantFallbackField;
            const quantityInput = productForm.querySelector('input[name="quantity"]');
            
            if (variantInput) {
                productData.variantId = variantInput.value;
            }
            
            if (quantityInput) {
                productData.quantity = parseInt(quantityInput.value) || 1;
            }
            
            if (productForm.dataset.finalSale === 'true') {
                productData.properties = {
                    ...(productData.properties || {}),
                    '_Not Returnable': 'Final Sale'
                };
            }
        }
        
        // Check for data attributes on the element
        if (element?.dataset) {
            if (element.dataset.variantId) {
                productData.variantId = element.dataset.variantId;
            }
            if (element.dataset.productId) {
                productData.productId = element.dataset.productId;
            }
            if (element.dataset.quantity) {
                productData.quantity = parseInt(element.dataset.quantity) || 1;
            }
        }
        
        // Default quantity if not specified
        if (!productData.quantity) {
            productData.quantity = 1;
        }

        if (productData.variantId) {
            const variantsScript = document.querySelector('[data-prodify-variants-json]');
            if (variantsScript?.textContent) {
                try {
                    const variants = JSON.parse(variantsScript.textContent);
                    const match = variants.find((variant) => String(variant.id) === String(productData.variantId));
                    if (match?.sku) {
                        productData.variantSku = match.sku;
                        productData.skus = [match.sku];
                    }
                } catch (error) {
                    if (window.enableDebugMode) console.warn('Unable to resolve variant SKU', error);
                }
            }
        }

        productData.mode = productData.mode || 'pdp';

        if (window.enableDebugMode) console.log('extracted data:', productData);
        
        return productData;
    }

    /**
     * Handle action trigger events
     */
    handleActionTrigger(event) {
        const { actionName, data } = event.detail;
        this.executeAction(actionName, data);
    }

    /**
     * Handle form submissions
     */
    handleFormSubmit(event) {
        const form = event.target;
        const actionName = form.dataset.action;
        
        if (actionName) {
            event.preventDefault();
            this.handleFormByAction(form, actionName);
        }
    }

    /**
     * Handle clicks on elements with data-action attributes
     */
    handleActionClick(event) {
        const element = event.target.closest('[data-action]');
        if (!element) return;

        const actionName = element.dataset.action;
        const actionData = this.parseActionData(element);

        event.preventDefault();
        this.executeAction(actionName, actionData);
    }

    /**
     * Handle form submission by action name
     */
    async handleFormByAction(form, actionName) {
        const formData = new window.FormData(form);
        const data = this.formDataToObject(formData);
        
        // Add form-specific data
        data.formElement = form;
        data.formId = form.id;
        
        await this.executeAction(actionName, data);
    }

    /**
     * Execute an action using the existing action system
     */
    async executeAction(actionName, data = {}) {
        try {
            if (window.enableDebugMode) {
                console.log(`[ActionHandler] Executing action: ${actionName}`, data);
            }

            // Handle special actions that don't require the triggerAction utility
            if (actionName === 'scrollToTop') {
                this.scrollToTop();
                return;
            }

            // Use the existing triggerAction utility for Shopify-specific actions
            await triggerAction(actionName, data);
            
        } catch (error) {
            console.error(`[ActionHandler] Error executing action ${actionName}:`, error);
            
            // Dispatch error event
            window.dispatchEvent(new CustomEvent('action:error', {
                detail: { actionName, error: error.message, data }
            }));
        }
    }

    /**
     * Scroll to top functionality
     */
    scrollToTop() {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    /**
     * Parse action data from element attributes
     */
    parseActionData(element) {
        const data = {};
        
        // Get all data-action-* attributes
        Array.from(element.attributes).forEach(attr => {
            if (attr.name.startsWith('data-action-')) {
                const key = attr.name.replace('data-action-', '').replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                data[key] = attr.value;
            }
        });

        // Parse JSON data if present
        if (element.dataset.actionData) {
            try {
                Object.assign(data, JSON.parse(element.dataset.actionData));
            } catch {
                console.warn('Failed to parse action data JSON:', element.dataset.actionData);
            }
        }

        return data;
    }

    /**
     * Convert FormData to plain object
     */
    formDataToObject(formData) {
        const obj = {};
        for (let [key, value] of formData.entries()) {
            if (obj[key]) {
                // Handle multiple values (checkboxes, etc.)
                if (Array.isArray(obj[key])) {
                    obj[key].push(value);
                } else {
                    obj[key] = [obj[key], value];
                }
            } else {
                obj[key] = value;
            }
        }
        return obj;
    }

    /**
     * Enable debug mode
     */
    enableDebug() {
        this.isDebug = true;
    }

    /**
     * Disable debug mode
     */
    disableDebug() {
        this.isDebug = false;
    }
}

// Create singleton instance
const actionHandler = new ActionHandler();

// Make globally available for debugging
if (typeof window !== 'undefined') {
    window.actionHandler = actionHandler;
}

export default actionHandler;